"use client"

import * as React from "react"
import { ToastUndoLabel } from "@ibada/ui"
import { toast } from "sonner"

import { MemberStatus } from "@/features/members/model/members-types"
import { useUpdateMemberMutation } from "@/features/members/services/members-query"
import { useTranslations } from "@/i18n"

/** The member a quick confirm acts on: its id, and the name the toast reads back. */
export type QuickConfirmTarget = { id: string; name: string }

export type QuickConfirmMember = {
  confirm: (target: QuickConfirmTarget) => void
  /** The record being confirmed (or un-confirmed) right now, if any. */
  confirmingId: string | null
}

/**
 * One-click confirm of a member awaiting confirmation: the same status change as the edit form, with an undo in the
 * toast. Shared by the members list, the members board "To confirm" tile and the work queue so the three never
 * drift. The server still decides (members EDIT on the member's own branch); callers only choose whether to offer
 * the button.
 *
 * `onChanged` runs after the confirm and after an undo, for a surface that caches the same record elsewhere (the
 * work queue drops the row it just confirmed).
 */
export function useQuickConfirmMember(options: { onChanged?: () => void } = {}): QuickConfirmMember {
  const tPreview = useTranslations("people.members.preview")
  const updateMember = useUpdateMemberMutation()
  const onChangedRef = React.useRef(options.onChanged)
  React.useEffect(() => {
    onChangedRef.current = options.onChanged
  }, [options.onChanged])

  const { mutate } = updateMember
  const confirm = React.useCallback(
    (target: QuickConfirmTarget) =>
      mutate(
        { id: target.id, patch: { status: MemberStatus.Active } },
        {
          onSuccess: () => {
            onChangedRef.current?.()
            toast.success(tPreview("confirmedToast", { name: target.name }), {
              // Long enough to reach the undo.
              duration: 8000,
              action: {
                label: React.createElement(ToastUndoLabel, null, tPreview("undo")),
                onClick: () =>
                  mutate(
                    { id: target.id, patch: { status: MemberStatus.PendingConfirmation } },
                    { onSuccess: () => onChangedRef.current?.() },
                  ),
              },
            })
          },
          onError: () => toast.error(tPreview("confirmFailed")),
        },
      ),
    [mutate, tPreview],
  )

  const confirmingId = updateMember.isPending ? (updateMember.variables?.id ?? null) : null
  return React.useMemo(() => ({ confirm, confirmingId }), [confirm, confirmingId])
}
